import { useTranslation } from "@redotech/redo-i18n";
import BarChartIcon from "@redotech/redo-icons/bar-chart-01.svg";
import classNames from "classnames";
import {
  type CSSProperties,
  type JSXElementConstructor,
  memo,
  ReactNode,
} from "react";
import { Flex } from "../../../flex";
import { InfoTooltip } from "../../../info-tooltip";
import { Text } from "../../../text";
import { SpacingValue } from "../../../theme/box";
import { RedoEmptyState } from "../../empty-state/redo-empty-state";
import { RedoListItem } from "../../list/redo-list";
import { RedoSpinner } from "../../loading/redo-spinner";
import { RedoSingleSelectDropdownInput } from "../../select-dropdown/redo-single-select-dropdown-input";
import * as styles from "./chart-card.module.css";

interface ChartCardBaseProps {
  title: string;
  /** Info icon next to the title, for context that doesn't fit in the title itself. */
  titleTooltip?: ReactNode;
  subtitle?: string;
  children: ReactNode;
  className?: string;
  minHeight?: string;
  minWidth?: string;
  contentPadding?: SpacingValue;
  /**
   * Header slot for tile overflow actions (retitle, change type, remove).
   * Rendered after the variant control. Callers own the menu.
   */
  actions?: ReactNode;
  /** When true, the content area shows `loadingStateContent` instead of children. */
  loading?: boolean;
  /** Defaults to a centered `RedoSpinner`. Pass a node to override. */
  loadingStateContent?: ReactNode;
  /**
   * When true (and not loading), the content area shows `emptyStateContent`
   * instead of children.
   */
  empty?: boolean;
  emptyStateContent?: ReactNode;
}

interface ChartCardSingleProps extends ChartCardBaseProps {
  variant: "single";
}

interface ChartCardMultiTypeProps<T> extends ChartCardBaseProps {
  variant: "multiType";
  options: RedoListItem<T>[];
  selectedItem: RedoListItem<T> | undefined;
  onOptionSelected: (params: { item: RedoListItem<T> }) => void;
}

interface ChartCardWithSecondaryTextProps extends ChartCardBaseProps {
  variant: "withSecondaryText";
  secondaryText?: ReactNode;
}

export type ChartCardProps<T> =
  | ChartCardSingleProps
  | ChartCardMultiTypeProps<T>
  | ChartCardWithSecondaryTextProps;

export const ChartCardLoadingStateContent = memo(
  function ChartCardLoadingStateContent() {
    return (
      <Flex
        align="center"
        data-arbiter="ChartCardLoadingStateContent"
        grow={1}
        height="full"
        justify="center"
        w="full"
      >
        <RedoSpinner size="md" style="track" />
      </Flex>
    );
  },
);

export const ChartCardEmptyStateContent = memo(
  function ChartCardEmptyStateContent({
    text: textProp,
    subtext: subtextProp,
    Icon = BarChartIcon,
  }: {
    text?: string;
    subtext?: string;
    Icon?: JSXElementConstructor<any>;
  }) {
    const { t } = useTranslation();
    const text = textProp ?? t("No data", { context: "empty chart" });
    const subtext =
      subtextProp ?? t("There's nothing to show for this period.");
    return (
      <Flex
        align="center"
        data-arbiter="ChartCardEmptyStateContent"
        grow={1}
        justify="center"
        w="full"
      >
        <RedoEmptyState
          Icon={Icon}
          iconColor="gray"
          subtext={subtext}
          text={text}
        />
      </Flex>
    );
  },
);

/**
 * Dashboard tile wrapper for palette charts. Compose as
 * `<ChartCard><RedoLineAndBarChart /></ChartCard>` — do not invent another
 * titled chart card. Conversion MetricChartCard / MetricLineChartCard /
 * MetricBarChartCard stay on existing pages until those pages are ported;
 * new tiles use ChartCard.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const ChartCard = memo(function ChartCard<T>({
  actions,
  children,
  className,
  contentPadding = "xl",
  empty = false,
  emptyStateContent,
  loading = false,
  loadingStateContent,
  minHeight = "260px",
  minWidth = "400px",
  subtitle,
  title,
  titleTooltip,
  ...props
}: ChartCardProps<T>) {
  const secondaryText =
    props.variant === "withSecondaryText" ? props.secondaryText : undefined;
  const hasSecondaryText = secondaryText != null;
  const hasTrailing =
    props.variant === "multiType" || hasSecondaryText || actions != null;

  const body = loading
    ? (loadingStateContent ?? <ChartCardLoadingStateContent />)
    : empty
      ? (emptyStateContent ?? <ChartCardEmptyStateContent />)
      : children;

  // CSS custom properties aren't in CSSProperties; the module reads these.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const cardVars = {
    "--chart-card-min-width": minWidth,
    "--chart-card-min-height": minHeight,
  } as CSSProperties;

  return (
    <Flex
      bgColor="primary"
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      className={classNames(styles.card, className)}
      data-arbiter="ChartCard"
      dir="column"
      flex={1}
      //redontIgnore
      style={cardVars}
      w="full"
    >
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        justify="space-between"
        p="xl"
        w="full"
      >
        <Flex dir="column" gap="xxs">
          <Flex align="center" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {title}
            </Text>
            {titleTooltip && (
              <InfoTooltip
                boxSize={14}
                tooltip={{ title: titleTooltip, leftAlign: true }}
              />
            )}
          </Flex>
          {subtitle && (
            <Text fontSize="xs" textColor="secondary">
              {subtitle}
            </Text>
          )}
        </Flex>
        {hasTrailing && (
          <Flex align="center" gap="sm" shrink={0}>
            {props.variant === "multiType" && (
              <RedoSingleSelectDropdownInput
                fitToAnchor={false}
                flexGrow={false}
                options={props.options}
                optionSelected={props.onOptionSelected}
                selectedItem={props.selectedItem}
                size="xs"
              />
            )}
            {props.variant === "withSecondaryText" &&
              hasSecondaryText &&
              (typeof secondaryText === "string" ? (
                <Text fontSize="xs" textColor="secondary">
                  {secondaryText}
                </Text>
              ) : (
                secondaryText
              ))}
            {actions}
          </Flex>
        )}
      </Flex>
      <Flex
        align={loading || empty ? "center" : undefined}
        className={classNames(styles.content, loading && styles.contentLoading)}
        flex="1"
        justify={loading || empty ? "center" : undefined}
        p={contentPadding}
      >
        {body}
      </Flex>
    </Flex>
  );
}) as <T>(props: ChartCardProps<T>) => ReactNode;
